import logoFull from "@/shared/assets/logo/logo-full.svg";

import type { MarkdownSection } from "@/shared/types/estateEssentials";

interface GenerateGuidePdfParams {
  title: string;
  preparedFor: string;
  sections: MarkdownSection[];
}

const BRAND_GREEN: [number, number, number] = [0, 136, 86];
const TEXT_PRIMARY: [number, number, number] = [28, 25, 23];
const TEXT_SECONDARY: [number, number, number] = [120, 113, 108];
const BORDER_COLOR: [number, number, number] = [214, 211, 209];
const TABLE_HEADER_BG: [number, number, number] = [240, 253, 244];

const MARGIN = 20;
const LINE_HEIGHT = 5.5;

function loadLogoAsDataUrl(): Promise<string> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext("2d")!;
      ctx.drawImage(img, 0, 0);
      resolve(canvas.toDataURL("image/png"));
    };
    img.src = logoFull;
  });
}

interface PdfState {
  doc: InstanceType<typeof import("jspdf").jsPDF>;
  y: number;
  pageWidth: number;
  pageHeight: number;
  contentWidth: number;
}

function checkPage(state: PdfState, needed: number) {
  if (state.y + needed > state.pageHeight - MARGIN) {
    state.doc.addPage();
    state.y = MARGIN;
  }
}

function addFooters(state: PdfState) {
  const totalPages = state.doc.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    state.doc.setPage(i);
    const footerY = state.pageHeight - 10;
    state.doc.setFontSize(7);
    state.doc.setFont("helvetica", "normal");
    state.doc.setTextColor(...TEXT_SECONDARY);
    state.doc.text("Generated by Elayne - elayne.co", MARGIN, footerY);
    state.doc.text(`Page ${i} of ${totalPages}`, state.pageWidth - MARGIN, footerY, {
      align: "right",
    });
  }
}

interface TableData {
  headers: string[];
  rows: string[][];
}

function parseTable(lines: string[]): TableData {
  const headers = lines[0]
    .split("|")
    .map((c) => c.trim())
    .filter(Boolean);
  const rows: string[][] = [];
  for (let i = 2; i < lines.length; i++) {
    const cells = lines[i]
      .split("|")
      .map((c) => c.trim())
      .filter(Boolean);
    if (cells.length > 0) rows.push(cells);
  }
  return { headers, rows };
}

function renderTable(state: PdfState, table: TableData) {
  const colCount = table.headers.length;
  const colWidth = state.contentWidth / colCount;
  const cellPadding = 3;
  const rowHeight = 8;

  checkPage(state, rowHeight * (table.rows.length + 1) + 4);

  state.doc.setDrawColor(...BORDER_COLOR);
  state.doc.setLineWidth(0.3);

  state.doc.setFillColor(...TABLE_HEADER_BG);
  state.doc.rect(MARGIN, state.y, state.contentWidth, rowHeight, "FD");

  state.doc.setFontSize(8);
  state.doc.setFont("helvetica", "bold");
  state.doc.setTextColor(...TEXT_PRIMARY);
  for (let i = 0; i < colCount; i++) {
    const x = MARGIN + colWidth * i + cellPadding;
    state.doc.text(table.headers[i], x, state.y + 5.5, {
      maxWidth: colWidth - cellPadding * 2,
    });
    if (i > 0) {
      state.doc.line(MARGIN + colWidth * i, state.y, MARGIN + colWidth * i, state.y + rowHeight);
    }
  }
  state.y += rowHeight;

  state.doc.setFont("helvetica", "normal");
  state.doc.setTextColor(...TEXT_SECONDARY);
  state.doc.setFontSize(8);

  for (const row of table.rows) {
    checkPage(state, rowHeight);
    state.doc.setDrawColor(...BORDER_COLOR);
    state.doc.line(MARGIN, state.y, MARGIN + state.contentWidth, state.y);

    for (let i = 0; i < colCount; i++) {
      const cellText = row[i] ?? "";
      const x = MARGIN + colWidth * i + cellPadding;
      const stripped = stripInlineMarkdown(cellText);
      state.doc.text(stripped, x, state.y + 5.5, {
        maxWidth: colWidth - cellPadding * 2,
      });
      if (i > 0) {
        state.doc.line(MARGIN + colWidth * i, state.y, MARGIN + colWidth * i, state.y + rowHeight);
      }
    }
    state.y += rowHeight;
  }

  state.doc.line(MARGIN, state.y, MARGIN + state.contentWidth, state.y);
  state.doc.rect(
    MARGIN,
    state.y - rowHeight * table.rows.length - rowHeight,
    state.contentWidth,
    rowHeight * (table.rows.length + 1),
  );
  state.y += 4;
}

function stripInlineMarkdown(text: string): string {
  return text
    .replace(/\*\*\*(.*?)\*\*\*/g, "$1")
    .replace(/\*\*(.*?)\*\*/g, "$1")
    .replace(/\*(.*?)\*/g, "$1")
    .replace(/`(.*?)`/g, "$1")
    .replace(/\[(.*?)\]\(.*?\)/g, "$1");
}

function renderSectionContent(state: PdfState, content: string) {
  const lines = content.split("\n");
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    if (line.trim() === "") {
      state.y += 2;
      i++;
      continue;
    }

    if (line.startsWith("|") && i + 1 < lines.length && lines[i + 1].includes("---")) {
      const tableLines: string[] = [];
      while (i < lines.length && lines[i].startsWith("|")) {
        tableLines.push(lines[i]);
        i++;
      }
      renderTable(state, parseTable(tableLines));
      continue;
    }

    if (line.startsWith("## ")) {
      checkPage(state, 14);
      state.y += 4;
      state.doc.setFontSize(11);
      state.doc.setFont("helvetica", "bold");
      state.doc.setTextColor(...TEXT_PRIMARY);
      state.doc.text(stripInlineMarkdown(line.slice(3).trim()), MARGIN, state.y);
      state.y += LINE_HEIGHT + 2;
      i++;
      continue;
    }

    if (line.startsWith("### ")) {
      checkPage(state, 12);
      state.y += 3;
      state.doc.setFontSize(10);
      state.doc.setFont("helvetica", "bold");
      state.doc.setTextColor(...TEXT_PRIMARY);
      state.doc.text(stripInlineMarkdown(line.slice(4).trim()), MARGIN, state.y);
      state.y += LINE_HEIGHT + 1;
      i++;
      continue;
    }

    if (line.startsWith("---")) {
      checkPage(state, 6);
      state.y += 2;
      state.doc.setDrawColor(...BORDER_COLOR);
      state.doc.setLineWidth(0.3);
      state.doc.line(MARGIN, state.y, state.pageWidth - MARGIN, state.y);
      state.y += 4;
      i++;
      continue;
    }

    if (line.startsWith("> ")) {
      checkPage(state, 10);
      const quoteText = stripInlineMarkdown(line.slice(2).trim());
      state.doc.setDrawColor(...BRAND_GREEN);
      state.doc.setLineWidth(1);
      state.doc.line(MARGIN, state.y - 1, MARGIN, state.y + 5);
      state.doc.setFontSize(9);
      state.doc.setFont("helvetica", "italic");
      state.doc.setTextColor(...TEXT_SECONDARY);
      const wrapped = state.doc.splitTextToSize(quoteText, state.contentWidth - 8);
      state.doc.text(wrapped, MARGIN + 5, state.y);
      state.y += wrapped.length * LINE_HEIGHT + 2;
      i++;
      continue;
    }

    const checkboxMatch = line.match(/^- \[([ x])\] (.+)/);
    if (checkboxMatch) {
      checkPage(state, 8);
      const checked = checkboxMatch[1] === "x";
      const text = stripInlineMarkdown(checkboxMatch[2]);

      state.doc.setDrawColor(...BORDER_COLOR);
      state.doc.setLineWidth(0.3);
      state.doc.rect(MARGIN + 2, state.y - 3, 3, 3);
      if (checked) {
        state.doc.setDrawColor(...BRAND_GREEN);
        state.doc.setLineWidth(0.5);
        state.doc.line(MARGIN + 2.5, state.y - 1.5, MARGIN + 3.5, state.y - 0.5);
        state.doc.line(MARGIN + 3.5, state.y - 0.5, MARGIN + 4.8, state.y - 2.5);
      }

      state.doc.setFontSize(9);
      state.doc.setFont("helvetica", "normal");
      state.doc.setTextColor(...TEXT_PRIMARY);
      const wrapped = state.doc.splitTextToSize(text, state.contentWidth - 10);
      state.doc.text(wrapped, MARGIN + 8, state.y);
      state.y += wrapped.length * LINE_HEIGHT + 1;
      i++;
      continue;
    }

    const bulletMatch = line.match(/^(\s*)[-*] (.+)/);
    if (bulletMatch) {
      checkPage(state, 8);
      const indent = Math.floor(bulletMatch[1].length / 2) * 4;
      const text = stripInlineMarkdown(bulletMatch[2]);

      state.doc.setFontSize(9);
      state.doc.setFont("helvetica", "normal");
      state.doc.setTextColor(...TEXT_PRIMARY);
      state.doc.text("\u2022", MARGIN + 2 + indent, state.y);
      const wrapped = state.doc.splitTextToSize(text, state.contentWidth - 8 - indent);
      state.doc.text(wrapped, MARGIN + 6 + indent, state.y);
      state.y += wrapped.length * LINE_HEIGHT + 1;
      i++;
      continue;
    }

    const numberedMatch = line.match(/^(\d+)\.\s+(.+)/);
    if (numberedMatch) {
      checkPage(state, 8);
      const num = numberedMatch[1];
      const text = stripInlineMarkdown(numberedMatch[2]);

      state.doc.setFontSize(9);
      state.doc.setFont("helvetica", "bold");
      state.doc.setTextColor(...TEXT_PRIMARY);
      state.doc.text(`${num}.`, MARGIN + 2, state.y);
      state.doc.setFont("helvetica", "normal");
      const wrapped = state.doc.splitTextToSize(text, state.contentWidth - 10);
      state.doc.text(wrapped, MARGIN + 8, state.y);
      state.y += wrapped.length * LINE_HEIGHT + 1;
      i++;
      continue;
    }

    checkPage(state, 8);
    state.doc.setFontSize(9);
    state.doc.setFont("helvetica", "normal");
    state.doc.setTextColor(...TEXT_SECONDARY);
    const text = stripInlineMarkdown(line.trim());
    const wrapped = state.doc.splitTextToSize(text, state.contentWidth);
    state.doc.text(wrapped, MARGIN, state.y);
    state.y += wrapped.length * LINE_HEIGHT + 1;
    i++;
  }
}

async function buildGuidePdf(params: GenerateGuidePdfParams) {
  const { title, preparedFor, sections } = params;
  const { default: jsPDF } = await import("jspdf");
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.width;
  const pageHeight = doc.internal.pageSize.height;
  const contentWidth = pageWidth - 2 * MARGIN;

  const state: PdfState = { doc, y: MARGIN, pageWidth, pageHeight, contentWidth };

  const logoDataUrl = await loadLogoAsDataUrl();
  const logoHeight = 8;
  const logoWidth = logoHeight * (1701 / 441);
  doc.addImage(logoDataUrl, "PNG", MARGIN, state.y, logoWidth, logoHeight);
  state.y += logoHeight + 8;

  doc.setDrawColor(...BORDER_COLOR);
  doc.setLineWidth(0.5);
  doc.line(MARGIN, state.y, pageWidth - MARGIN, state.y);
  state.y += 10;

  doc.setFontSize(20);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...TEXT_PRIMARY);
  doc.text(title, MARGIN, state.y);
  state.y += 6;

  doc.setFontSize(9);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(...TEXT_SECONDARY);
  const dateStr = new Date().toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
  doc.text(`Prepared for: ${preparedFor} - ${dateStr}`, MARGIN, state.y + 4);
  state.y += 14;

  for (const section of sections) {
    checkPage(state, 20);

    doc.setFontSize(13);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...TEXT_PRIMARY);
    doc.text(section.title, MARGIN, state.y);

    state.y += 3;
    doc.setDrawColor(...BRAND_GREEN);
    doc.setLineWidth(0.8);
    doc.line(MARGIN, state.y, pageWidth - MARGIN, state.y);
    state.y += 8;

    renderSectionContent(state, section.content);
    state.y += 6;
  }

  addFooters(state);

  return { doc, title };
}

export async function generateGuidePdf(params: GenerateGuidePdfParams) {
  const { doc, title } = await buildGuidePdf(params);
  const fileName = title
    .replace(/[^a-zA-Z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .toLowerCase();
  doc.save(`${fileName}.pdf`);
}

export async function printGuidePdf(params: GenerateGuidePdfParams) {
  const { doc } = await buildGuidePdf(params);
  const blob = doc.output("blob");
  const url = URL.createObjectURL(blob);
  const printWindow = window.open(url, "_blank");
  if (printWindow) {
    printWindow.onload = () => {
      printWindow.focus();
      printWindow.print();
      URL.revokeObjectURL(url);
    };
  } else {
    URL.revokeObjectURL(url);
  }
}
